import React, { memo, useEffect, useState } from 'react';
import { Typography } from '@saviynt/design-system';

import { getAbbreviatedNumber } from '../../utils';

interface CountUpProps {
  target?: number;
  duration?: number;
  delay?: number;
  className?: string;
  isAnimation?: boolean | null;
  isSecondary?: boolean;
}

/**
 * CountUp component animates a numerical value from 0 to a specified target over a given duration,
 * with optional delay and formatting for thousands (K), millions (M), and billions (B).
 *
 * - Values under 1,000 are displayed as integers with no decimal.
 * - Values over 1,000 are abbreviated (e.g., 1.2K, 3.5M, 7.8B).
 * - The animation uses an ease-in-out timing function for a smooth appearance.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {number} [props.target=0] - Final number to count up to
 * @param {number} [props.duration=800] - Animation duration in milliseconds
 * @param {number} [props.delay=0] - Delay before starting the animation in milliseconds
 * @param {string} [props.className=''] - Optional className to apply to the Typography element
 * @param {boolean} [props.isAnimation] - Optional boolean to trigger animation
 *
 * @returns {JSX.Element} A Typography element displaying the animated count
 */
function CountUp({
  target = 0,
  duration = 800,
  delay = 0,
  className = '',
  isAnimation = true,
  isSecondary,
}: CountUpProps) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number | null = null;

    const easeInOut = (t: number): number =>
      t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const startAnimation = () => {
      const step = (timestamp: number) => {
        if (!startTime) {
          startTime = timestamp;
        }

        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOut(progress);
        const currentValue = target * easedProgress;

        setCount(currentValue);

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    };

    if (!isAnimation) {
      setCount(target);

      return;
    }

    const delayTimeout = setTimeout(startAnimation, delay);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(delayTimeout);
  }, [target, duration, delay, isAnimation]);

  return (
    <Typography
      kind={isSecondary ? 'display4' : 'display3'}
      className={className}>
      {getAbbreviatedNumber(count)}
    </Typography>
  );
}

CountUp.defaultProps = {
  target: 0,
  duration: 800,
  delay: 0,
  className: '',
  isAnimation: true,
  isSecondary: false,
};

export default memo(CountUp);
